"use client";

import { useDeferredValue, useMemo, useState } from "react";
import { Notice, TextAreaField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  type CheckerLabels,
  CheckerReport,
} from "@/components/tools/reference-checkers/checker-report";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  AMA_SOURCES,
  type AmaReport,
  type AmaRule,
  checkAmaReferences,
} from "@/lib/citation-check-ama";
import { t, tPlural } from "@/lib/i18n/interpolate";

/** English strings. A translated content file overrides these through `ui`. */
export const CITATION_CHECKER_AMA_STRINGS = {
  inputLabel: "AMA reference list",
  inputPlaceholder:
    "1. Author AA, Author BB, Author CC. Title of article. Abbreviated Journal Title. Year;volume(issue):page-page. doi:10.xxxx",
  inputHint:
    "One reference per line, or wrapped lines under their number. The check runs in your browser — nothing is uploaded.",
  trySample: "Try a sample",
  clear: "Clear",
  sample:
    '1. Quiroz YT, Schultz AP, Chen K, et al. Brain imaging and blood biomarker abnormalities in children with autosomal dominant Alzheimer disease: a cross-sectional study. JAMA Neurol. 2015;72(8):912-919. doi:10.1001/jamaneurol.2015.1099\n2. Rose ME, Huerbin MB, Melick J, Marion DW, Palmer AM, Schiding JK, Kochanek PM, Graham SH. Regulation of interstitial excitatory amino acid concentrations after cortical contusion injury. Brain Res. 2002;935(1-2):40-6. doi: 10.1016/s0006-8993(02)02445-9.\n3. Lohela T. J., Lilius T. O., and Nedergaard M. "The Glymphatic System: Implications for Drugs for Central Nervous System Diseases." Nat. Rev. Drug Discov. 2022; 21(10): 763-779. https://doi.org/10.1038/s41573-022-00500-9\n4. Halpern SD, Ubel PA, Caplan AL. Solid-organ transplantation in HIV-infected patients. N Engl J Med. 2002 Jul 25;347(4):284-7.',
  emptyPrompt: "Paste an AMA reference list to check it.",
  results: "Results",
  summary: "{issues} across {entries}",
  issuesCount:
    "one:{count} likely issue to review|other:{count} likely issues to review",
  entriesCount: "one:{count} reference|other:{count} references",
  noIssues: "No likely issues found in {entries}.",
  noIssuesBody:
    "This is a first pass, not a guarantee. The checker reads the punctuation, order and abbreviations of each reference; it cannot see italics, and it cannot tell whether a source exists or whether its details are right.",
  cleanCount:
    "one:{count} reference had no likely issues.|other:{count} references had no likely issues.",
  listHeading: "The list as a whole",
  entryLabel: "Reference {label}",
  confidence: { high: "Likely issue", medium: "Worth a look" },
  suggestionLabel: "Suggested:",
  copyFix: "Copy fix",
  copied: "Copied!",
  why: "Why: {source} →",
  sources: {
    manual: "AMA Manual of Style, 11th ed.",
    authors: "AMA 11 authors and DOIs (WVU Libraries)",
    journals: "AMA 11 journal references (NYMC Library)",
  },
  ruleTitles: {
    unnumbered: "References are not numbered",
    "numbering-missing": "Some references have no number",
    "numbering-duplicate": "A reference number is used twice",
    "numbering-start": "The list does not start at 1",
    "numbering-gap": "Numbers are missing from the sequence",
    "numbering-order": "Numbers are out of sequence",
    "duplicate-entry": "The same source may be listed twice",
    "alphabetical-order": "The list looks alphabetical",
    "author-periods": "Periods in the initials",
    "author-comma": "Comma between surname and initials",
    "author-natural": "Initials before the surname",
    "author-and": "“and” or “&” between names",
    "et-al-threshold": "More than six authors listed",
    "et-al-count": "et al. after a number of names other than three",
    "title-quotes": "Article title in quotation marks",
    "title-case": "Article title in Title Case",
    "journal-periods": "Periods in the journal abbreviation",
    "ieee-locators": "IEEE-style vol., no. and pp.",
    "locator-spacing": "Spaces in the year, volume and pages",
    "locator-date": "Month and day before the volume",
    "page-range": "Page range shortened",
    "doi-format": "DOI not written “doi:10.…”",
    "link-period": "Period after the closing DOI or URL",
    "terminal-period": "Reference does not end with a period",
  } satisfies Record<AmaRule, string>,
  ruleMessages: {
    unnumbered:
      "AMA numbers references consecutively in the order they are first cited in the text. None of these references has a number.",
    "numbering-missing":
      "Every reference in an AMA list has a number. These entries, counting from the top, have none: {entries}.",
    "numbering-duplicate":
      "{number} appears more than once. Each number belongs to exactly one reference.",
    "numbering-start":
      "The first number is {first}. A complete list starts at 1 — ignore this if you pasted only part of your list.",
    "numbering-gap":
      "These numbers are missing: {numbers}. If a reference was removed, renumber the list and the superscript citations in your text.",
    "numbering-order":
      "{number} comes after {previous}. An AMA list runs in numerical order, and the numbers follow the order of first citation.",
    "duplicate-entry":
      "References {first} and {second} look like the same source (identical text or the same DOI). Give each source one number and reuse it every time you cite it.",
    "alphabetical-order":
      "These references run A–Z by first author. AMA numbers references in the order they are first cited, so an alphabetical list usually means numbers were added to an author–date list.",
    "author-periods":
      "AMA writes initials with no periods or spaces: Lohela TJ, not Lohela T. J.",
    "author-comma":
      "There is no comma between a surname and its initials in AMA — Smith JA, not Smith, J. A. Commas separate one author from the next.",
    "author-natural":
      "AMA puts the surname first, then the initials: Smith JA, not J. A. Smith.",
    "author-and":
      "AMA separates names with commas and does not put “and” or “&” between them.",
    "et-al-threshold":
      "AMA lists every author up to six. With more than six — this reference names {count} — give the first three, then et al.",
    "et-al-count":
      "AMA uses et al. only when there are more than six authors, and then after exactly three names. This reference has {count} before et al.: list every author if there are six or fewer, otherwise the first three.",
    "title-quotes": "AMA article titles take no quotation marks.",
    "title-case":
      "AMA article titles are in sentence case: capitalize the first word, proper nouns, abbreviations and the names of trials or study groups. Every main word of this title is capitalized.",
    "journal-periods":
      "Write {expected} rather than {found} — AMA abbreviates journal names as the NLM Catalog does, with no periods. They are also italic, which a plain-text check cannot see.",
    "ieee-locators":
      "vol., no. and pp. are IEEE labels. AMA writes Year;Volume(Issue):Pages, as in 2015;72(8):912-919.",
    "locator-spacing":
      "AMA sets the year, volume, issue and pages without spaces: {expected}, not {found}.",
    "locator-date":
      "AMA journal references give the year alone before the volume; a month and day there is the NLM (Vancouver) form. Expected {expected}, not {found}.",
    "page-range":
      "AMA does not omit digits from page ranges: {expected}, not {found}.",
    "doi-format":
      "AMA writes the DOI as “doi:” followed directly by the DOI — no space and no https://doi.org/ — as the last element, with no period after it: {expected}",
    "link-period":
      "AMA puts no period after a DOI or URL that ends a reference, so the link still works when clicked. End with {expected}",
    "terminal-period":
      "An AMA reference ends with a period unless its last element is a DOI or URL.",
  } satisfies Record<AmaRule, string>,
  checksHeading: "What this checker looks for",
  checks: [
    "Numbering in citation order, with no gaps, repeats or numbers out of sequence.",
    "Authors as Smith JA, separated by commas with no “and”: every name up to six; more than six, the first three and et al.",
    "Article titles in sentence case without quotation marks, and journal abbreviations without periods.",
    "The block Year;Volume(Issue):Pages with no spaces, no month or day, and full page ranges (912-919).",
    "DOIs written doi:10.xxxx as the last element, with no period after them, and a period at the end of every other reference.",
    "The same source listed twice, and lists sorted A–Z instead of by first citation.",
  ],
  limits:
    "It cannot see italics, so it does not check that journal names are italic, and it does not check abbreviations against the NLM Catalog. Every finding is a likely issue to review, not a verdict; your list is never rewritten for you.",
} as const;

type Strings = typeof CITATION_CHECKER_AMA_STRINGS;

function reportLabels(
  report: AmaReport,
  strings: Strings,
  locale: string,
  fmt: (value: number) => string
): CheckerLabels {
  const entries = tPlural(strings.entriesCount, locale, report.totalEntries, {
    count: fmt(report.totalEntries),
  });
  const issues = tPlural(strings.issuesCount, locale, report.totalFindings, {
    count: fmt(report.totalFindings),
  });
  const clean = report.entries.filter(
    (entry) => entry.findings.length === 0
  ).length;
  return {
    results: strings.results,
    summary:
      report.totalFindings === 0
        ? t(strings.noIssues, { entries })
        : t(strings.summary, { issues, entries }),
    noIssues: t(strings.noIssues, { entries }),
    noIssuesBody: strings.noIssuesBody,
    listHeading: strings.listHeading,
    entryLabel: strings.entryLabel,
    confidence: strings.confidence,
    suggestionLabel: strings.suggestionLabel,
    copyFix: strings.copyFix,
    copied: strings.copied,
    why: strings.why,
    cleanNote:
      report.totalFindings > 0 && clean > 0
        ? tPlural(strings.cleanCount, locale, clean, { count: fmt(clean) })
        : "",
  };
}

export default function CitationCheckerAmaWidget() {
  const strings = useToolUi(CITATION_CHECKER_AMA_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const deferred = useDeferredValue(text);
  const report = useMemo(
    () => (deferred.trim() ? checkAmaReferences(deferred) : null),
    [deferred]
  );
  const sources = useMemo(
    () => ({
      manual: { url: AMA_SOURCES.manual, label: strings.sources.manual },
      authors: { url: AMA_SOURCES.authors, label: strings.sources.authors },
      journals: { url: AMA_SOURCES.journals, label: strings.sources.journals },
    }),
    [strings]
  );

  return (
    <>
      <TextAreaField
        hint={strings.inputHint}
        id="ama-check-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {report ? (
        <CheckerReport
          fmt={fmt}
          labels={reportLabels(report, strings, locale, fmt)}
          messages={strings.ruleMessages}
          report={report}
          sources={sources}
          titles={strings.ruleTitles}
        />
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H3}>{strings.checksHeading}</h2>
        <ul className="list-disc space-y-2 pl-5 text-muted-foreground text-sm leading-relaxed">
          {strings.checks.map((check) => (
            <li key={check}>{check}</li>
          ))}
        </ul>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.limits}
        </p>
      </section>
    </>
  );
}
